<template>
	<div>
		<page title="Statistics">
			<v-row>
				<v-col class="pt-sm-0">
					<v-sheet rounded>
						<v-card color="#125B92" elevation="0" class="rounded-b-0 white--loader">
							<v-card-title class="align-center">
								<v-icon left dark>mdi-chart-bar</v-icon>
								<h3 class="white--text">
									All Time - Invoice History
								</h3>
								<v-spacer />
							</v-card-title>
							<v-divider />
						</v-card>

						<div class="paid-chart">
							<div class="paid-body">
								<div v-if="! pageLoaded" class="chart-loading">
									<v-overlay absolute opacity="0.75" color="#fff">
										<v-progress-circular indeterminate size="32" color="#125b92"></v-progress-circular>
									</v-overlay>
								</div>
								<apexchart
									v-else
									ref="totalChart"
									type="bar"
									height="320"
									:options="totalChartOptions"
									:series="totalGraphSeries" />
							</div>
						</div>
					</v-sheet>
				</v-col>
				<v-col cols="12"
					sm="6"
					md="6"
					lg="3"
					class="pt-sm-0">
					<v-sheet rounded>
						<v-card color="#125B92" elevation="0" class="rounded-b-0 white--loader" :loading="loading && Object.values(counts).some(({ all }) => all === 0)">
							<v-card-title class="align-center">
								<v-icon left dark>mdi-chart-pie-outline</v-icon>
								<h3 class="white--text">All Time - Overview</h3>
								<v-spacer />
							</v-card-title>
							<v-divider />
							<v-list>
								<v-list-item>
									<v-list-item-content>
										<span class="text-h6 text--secondary">Projects</span>
									</v-list-item-content>
									<v-list-item-action>
										<div class="text-h6 font-mono text--primary">{{ counts.projects.all | numeric }}</div>
									</v-list-item-action>
								</v-list-item>
								<v-divider />
								<v-list-item>
									<v-list-item-content>
										<span class="text-h6 text--secondary">Properties</span>
									</v-list-item-content>
									<v-list-item-action>
										<div class="text-h6 font-mono text--primary">{{ counts.properties.all | numeric }}</div>
									</v-list-item-action>
								</v-list-item>
								<v-divider />
								<v-list-item>
									<v-list-item-content>
										<span class="text-h6 text--secondary">Clients</span>
									</v-list-item-content>
									<v-list-item-action>
										<div class="text-h6 font-mono text--primary">{{ counts.clients.all | numeric }}</div>
									</v-list-item-action>
								</v-list-item>
								<v-divider />
								<v-list-item>
									<v-list-item-content>
										<span class="text-h6 text--secondary">Users</span>
									</v-list-item-content>
									<v-list-item-action>
										<div class="text-h6 font-mono text--primary">{{ counts.users.all | numeric }}</div>
									</v-list-item-action>
								</v-list-item>
							</v-list>
						</v-card>
					</v-sheet>
				</v-col>
			</v-row>

			<v-row>
				<v-col cols="12" md="3">
					<v-select
						v-model="selectedYear"
						:items="years"
						filled
						hide-details
						dense
						class="cursor-pointer"
						background-color="white"
						label="Revenue Stats by Year"
						@input="reload(false)" />
				</v-col>
				<v-col cols="12" md="3">
					<v-select
						v-model="selectedQuarter"
						:items="selectableQuarters"
						filled
						hide-details
						dense
						class="cursor-pointer"
						background-color="white"
						label="Filter By Quarter"
						@input="reload(false)" />
				</v-col>
				<v-col class="text-right">
					<v-tooltip left>
						<template #activator="{ on, attrs }">
							<v-avatar
								color="primary"
								class="cursor-pointer"
								dark
								large
								v-bind="attrs"
								@click="reload(true)"
								v-on="on">
								<v-icon color="white">mdi-refresh</v-icon>
							</v-avatar>
						</template>
						<span>Stats are cached, recalculate?</span>
					</v-tooltip>
				</v-col>
			</v-row>

			<v-row>
				<v-col cols="12"
					sm="12"
					md="12"
					lg="8"
					class="pt-sm-0">
					<v-sheet rounded>
						<v-card color="#125B92" elevation="0" class="rounded-b-0 white--loader" :loading="loading">
							<v-card-title class="align-center">
								<v-icon left dark>mdi-bank</v-icon>
								<h3 class="white--text">
									Revenue Statistics
								</h3>
								<v-spacer />
							</v-card-title>
							<v-divider />
							<v-list class="revenue-stats-list">
								<v-list-item
									:class="[
										'revenue-stats-row',
										'revenue-stats-header',
										{ 'revenue-stats-row--with-today-calc': hasTodayCalc }
									]">
									<div>Status</div>
									<div>
										{{ selectedYear ?? year }} {{ selectedQuarter ? 'Q' + selectedQuarter : null }}{{ hasTodayCalc ? ' YTD' : '' }}
									</div>
									<div v-if="hasTodayCalc">
										{{ (selectedYear ?? year) - 1 }} {{ selectedQuarter ? 'Q' + selectedQuarter : null }} YTD
									</div>
									<div>{{ (selectedYear ?? year) - 1 }} {{ selectedQuarter ? 'Q' + selectedQuarter : null }}</div>
								</v-list-item>

								<v-divider />
								<v-list-item
									:class="[
										'revenue-stats-row',
										{ 'revenue-stats-row--with-today-calc': hasTodayCalc }
									]">
									<div class="text-h6 text--secondary">
										Invoices <strong>Outstanding</strong>
										<span
											v-if="revenueChanges.ytd_outstanding"
											:class="[
												'revenue-change ml-1',
												`revenue-change--${revenueChanges.ytd_outstanding.direction}`,
											]">
											<v-icon size="15" :color="'up' === revenueChanges.ytd_outstanding.direction ? '#16a794' : '#ff6c6c'">
												{{ 'up' === revenueChanges.ytd_outstanding.direction ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
											</v-icon>
											{{ revenueChanges.ytd_outstanding.percentage }}%
										</span>
									</div>
									<div class="text-h6 font-mono text--primary">
										{{ revenue.ytd_outstanding | money }}
									</div>
									<div v-if="hasTodayCalc" class="text-h6 font-mono text--secondary">
										{{ lastRevenue.today_calc.ytd_outstanding | money }}
									</div>
									<div class="text-h6 font-mono text--secondary">
										{{ lastRevenue.ytd_outstanding | money }}
									</div>
								</v-list-item>

								<v-divider />
								<v-list-item
									:class="[
										'revenue-stats-row',
										{ 'revenue-stats-row--with-today-calc': hasTodayCalc }
									]">
									<div class="text-h6 text--secondary">
										Invoices <strong>Total</strong>
										<span
											v-if="revenueChanges.ytd_total"
											:class="[
												'revenue-change ml-1',
												`revenue-change--${revenueChanges.ytd_total.direction}`,
											]">
											<v-icon size="15" :color="'up' === revenueChanges.ytd_total.direction ? '#16a794' : '#ff6c6c'">
												{{ 'up' === revenueChanges.ytd_total.direction ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
											</v-icon>
											{{ revenueChanges.ytd_total.percentage }}%
										</span>
									</div>
									<div class="text-h6 font-mono text--primary">
										{{ revenue.ytd_total | money }}
									</div>
									<div v-if="hasTodayCalc" class="text-h6 font-mono text--secondary">
										{{ lastRevenue.today_calc.ytd_total | money }}
									</div>
									<div class="text-h6 font-mono text--secondary">
										{{ lastRevenue.ytd_total | money }}
									</div>
								</v-list-item>

								<v-divider />
								<v-list-item
									:class="[
										'revenue-stats-row',
										{ 'revenue-stats-row--with-today-calc': hasTodayCalc }
									]">
									<div class="text-h6 text--secondary">
										Payments <strong>Received</strong>
										<span
											v-if="revenueChanges.ytd_paid"
											:class="[
												'revenue-change ml-1',
												`revenue-change--${revenueChanges.ytd_paid.direction}`,
											]">
											<v-icon size="15" :color="'up' === revenueChanges.ytd_paid.direction ? '#16a794' : '#ff6c6c'">
												{{ 'up' === revenueChanges.ytd_paid.direction ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
											</v-icon>
											{{ revenueChanges.ytd_paid.percentage }}%
										</span>
									</div>
									<div class="text-h6 font-mono text--primary">
										{{ revenue.ytd_paid | money }}
									</div>
									<div v-if="hasTodayCalc" class="text-h6 font-mono text--secondary">
										{{ lastRevenue.today_calc.ytd_paid | money }}
									</div>
									<div class="text-h6 font-mono text--secondary">
										{{ lastRevenue.ytd_paid | money }}
									</div>
								</v-list-item>

								<v-divider />
								<v-list-item
									:class="[
										'revenue-stats-row',
										{ 'revenue-stats-row--with-today-calc': hasTodayCalc }
									]">
									<div class="text-h6 text--secondary">
										Invoices <strong>Drafted</strong>
										<span
											v-if="revenueChanges.ytd_draft"
											:class="[
												'revenue-change ml-1',
												`revenue-change--${revenueChanges.ytd_draft.direction}`,
											]">
											<v-icon size="15" :color="'up' === revenueChanges.ytd_draft.direction ? '#16a794' : '#ff6c6c'">
												{{ 'up' === revenueChanges.ytd_draft.direction ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
											</v-icon>
											{{ revenueChanges.ytd_draft.percentage }}%
										</span>
									</div>
									<div class="text-h6 font-mono text--primary">
										{{ revenue.ytd_draft | money }}
									</div>
									<div v-if="hasTodayCalc" class="text-h6 font-mono text--secondary">
										{{ lastRevenue.today_calc.ytd_draft | money }}
									</div>
									<div class="text-h6 font-mono text--secondary">
										{{ lastRevenue.ytd_draft | money }}
									</div>
								</v-list-item>
							</v-list>
						</v-card>
					</v-sheet>
				</v-col>
				<v-col cols="12"
					sm="6"
					md="6"
					lg="4"
					class="pt-sm-0">
					<v-sheet rounded>
						<v-card color="#125B92" elevation="0" class="rounded-b-0 white--loader" :loading="loading">
							<v-card-title class="align-center">
								<v-icon left dark>mdi-chart-pie-outline</v-icon>
								<h3 class="white--text">
									{{ selectedYear ?? year }} {{ selectedQuarter ? 'Q' + selectedQuarter : null }}
									- Overview
								</h3>
								<v-spacer />
							</v-card-title>
							<v-divider />
							<v-list>
								<v-list-item>
									<v-list-item-content>
										<span class="text-h6 text--secondary">New Projects</span>
									</v-list-item-content>
									<v-list-item-action>
										<div class="text-h6 font-mono text--primary">{{ counts.projects.ytd | numeric }}</div>
									</v-list-item-action>
								</v-list-item>
								<v-divider />
								<v-list-item>
									<v-list-item-content>
										<span class="text-h6 text--secondary">New Properties</span>
									</v-list-item-content>
									<v-list-item-action>
										<div class="text-h6 font-mono text--primary">{{ counts.properties.ytd | numeric }}</div>
									</v-list-item-action>
								</v-list-item>
								<v-divider />
								<v-list-item>
									<v-list-item-content>
										<span class="text-h6 text--secondary">New Clients</span>
									</v-list-item-content>
									<v-list-item-action>
										<div class="text-h6 font-mono text--primary">{{ counts.clients.ytd | numeric }}</div>
									</v-list-item-action>
								</v-list-item>
								<v-divider />
								<v-list-item>
									<v-list-item-content>
										<span class="text-h6 text--secondary">New Users</span>
									</v-list-item-content>
									<v-list-item-action>
										<div class="text-h6 font-mono text--primary">{{ counts.users.ytd | numeric }}</div>
									</v-list-item-action>
								</v-list-item>
							</v-list>
						</v-card>
					</v-sheet>
				</v-col>
			</v-row>

			<v-row>
				<v-col>
					<v-sheet rounded>
						<v-card color="#125B92" elevation="0" class="rounded-b-0">
							<v-card-title class="align-center">
								<h3 class="white--text">{{ selectedYear ?? year }} {{ selectedQuarter ? 'Q' + selectedQuarter : null }} Clients</h3>
								<v-spacer />
							</v-card-title>
							<client-table :clients="clients" :loading="loading" :year="selectedYear ?? year" :quarter="selectedQuarter" />
						</v-card>
					</v-sheet>
				</v-col>
			</v-row>
		</page>
	</div>
</template>
<script>
import Backend from '@/Layouts/Backend'
import Page from '@/Pages/Backend/Components/Page'
import ClientTable from '@/Pages/Backend/Stats/ClientTable'

export default {
	components: { Page, ClientTable },
	layout: Backend,
	props: {
		years: {
			type: [Array, Object],
			default: () => {},
			required: true
		},
		year: {
			type: Number,
			default: () => new Date().getFullYear()
		},
		types: {
			type: [Array, Object],
			default: () => {}
		},
		revenue: {
			type: Object,
			default: () => {
				return {
					ytd_outstanding: 0,
					ytd_payments: 0,
					ytd_paid: 0,
					ytd_total: 0,
					ytd_draft: 0
				}
			},
			required: false
		},
		lastRevenue: {
			type: Object,
			default: () => {
				return {
					ytd_outstanding: 0,
					ytd_payments: 0,
					ytd_paid: 0,
					ytd_total: 0,
					ytd_draft: 0
				}
			},
			required: false
		},
		counts: {
			type: Object,
			default: () => {
				return {
					projects: { all: 0, ytd: 0 },
					properties: { all: 0, ytd: 0 },
					clients: { all: 0, ytd: 0 },
					users: { all: 0, ytd: 0 }
				}
			}
		},
		clients: {
			type: [Array, Object],
			default: () => {},
			required: false
		},
		invoiceGraph: {
			type: Array,
			default: () => [],
			required: false
		}
	},
	data () {
		return {
			selectedYear: this.year,
			selectedQuarter: null,
			selectableQuarters: [
				{ value: null, text: 'All Year' },
				{ value: 1, text: 'Q1' },
				{ value: 2, text: 'Q2' },
				{ value: 3, text: 'Q3' },
				{ value: 4, text: 'Q4' }
			],
			loading: true,
			pageLoaded: false,
			totalChartOptions: {
				chart: {
					background: '#ffffff',
					toolbar: {
						show: false
					},
					events: {
						dataPointSelection: (event, chartContext, config) => {
							this.selectedYear = config.seriesIndex === 0
								? this.selectedYear - 1
								: this.selectedYear

							this.selectedQuarter = null
							this.reload()
						}
					}
				},
				colors: ['#125B92', '#4e843d'],
				legend: {
					show: true
				},
				dataLabels: {
					enabled: false
				},
				grid: {
					borderColor: '#e5e7eb'
				},
				plotOptions: {
					bar: {
						columnWidth: '55%'
					}
				},
				tooltip: {
					y: {
						formatter: (amount) => new Intl.NumberFormat('en-US', {
							style: 'currency',
							currency: 'USD'
						}).format(amount)
					}
				},
				xaxis: {
					categories: [],
					labels: {
						rotate: 0
					}
				},
				yaxis: {
					labels: {
						formatter: (amount) => new Intl.NumberFormat('en-US', {
							style: 'currency',
							currency: 'USD',
							maximumFractionDigits: 0
						}).format(amount)
					}
				}
			}
		}
	},
	computed: {
		revenueChanges () {
			return Object.keys(this.revenue).reduce((changes, field) => {
				const lastValue = this.hasTodayCalc ? this.lastRevenue.today_calc : this.lastRevenue

				const currentValue = Number(this.revenue[field]) || 0
				const previousValue = Number(lastValue[field]) || 0

				if (0 === previousValue || 0 === currentValue || currentValue === previousValue) {
					changes[field] = null

					return changes
				}

				const percentage = ((currentValue - previousValue) / Math.abs(previousValue)) * 100

				changes[field] = {
					direction: 0 < percentage ? 'up' : 'down',
					percentage: Number(Math.round(Math.abs(percentage))).toLocaleString('en-US')
				}

				return changes
			}, {})
		},
		totalGraphSeries () {
			return [
				{
					name: `Total Invoiced (${this.selectedYear - 1})`,
					data: this.invoiceGraph.map(({ month, previous }) => ({
						x: this.monthName(month),
						y: previous
					}))
				},
				{
					name: `Total Invoiced (${this.selectedYear})`,
					data: this.invoiceGraph.map(({ month, current }) => ({
						x: this.monthName(month),
						y: current
					}))
				}
			]
		},
		hasTodayCalc () {
			return this.lastRevenue.today_calc && Object.keys(this.lastRevenue.today_calc).length > 0
		}
	},
	mounted () {
		this.reload()
	},
	methods: {
		monthName (month) {
			return new Intl.DateTimeFormat('en-US', {
				month: 'long'
			}).format(new Date(2000, month - 1))
		},
		reload (force = false) {
			this.$inertia.reload({
				data: { year: this.selectedYear, quarter: this.selectedQuarter, force: force },
				only: ['types', 'revenue', 'lastRevenue', 'counts', 'clients', 'invoiceGraph'],
				onBefore: () => this.loading = true,
				onFinish: () => {
					this.loading = false
					this.pageLoaded = true

					this.$nextTick(() => {
						this.$refs.totalChart.updateOptions({
							series: this.totalGraphSeries
						})
					})
				}
			})
		}
	}
}
</script>
